import React, { FC, useState, useEffect } from "react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { FiFilter } from "react-icons/fi";
import { Label } from "../ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

type MainFilterOption = "None" | "Channel" | "Campaign Status" | "Setup Preview" | "Accounts";
type SubFilterOption = string;

const AdminCampaignListDropdown: FC<{
  setFilterData: React.Dispatch<
    React.SetStateAction<{ filter: string; value: string }>
  >;
  filterData: { filter: string; value: string };
}> = ({ setFilterData, filterData }) => {
  const [selectedMainFilter, setSelectedMainFilter] =
    useState<MainFilterOption>("None");
  const [selectedSubFilter, setSelectedSubFilter] =
    useState<SubFilterOption>("");
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [accountSearchTerm, setAccountSearchTerm] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);

  // Sync with parent
  useEffect(() => {
    setSelectedMainFilter(filterData.filter as MainFilterOption);
    
    // Parse selected channels if filter is Channel
    if (filterData.filter === "Channel" && filterData.value) {
      setSelectedChannels(filterData.value.split(","));
      setSelectedSubFilter("");
      setAccountSearchTerm("");
    } else if (filterData.filter === "Campaign Status" && filterData.value) {
      // Convert lowercase from API back to title case for display
      const displayValue = filterData.value.charAt(0).toUpperCase() + filterData.value.slice(1);
      setSelectedSubFilter(displayValue);
      setSelectedChannels([]);
      setAccountSearchTerm("");
    } else if (filterData.filter === "Setup Preview" && filterData.value) {
      // Set the selectedSubFilter to the actual value for proper selection matching
      setSelectedSubFilter(filterData.value);
      setSelectedChannels([]);
      setAccountSearchTerm("");
    } else if (filterData.filter === "Accounts" && filterData.value) {
      setAccountSearchTerm(filterData.value);
      setSelectedSubFilter("");
      setSelectedChannels([]);
    } else {
      setSelectedSubFilter(filterData.value);
      setSelectedChannels([]);
      setAccountSearchTerm("");
    }
  }, [filterData]);

  // Handle main filter change
  const handleMainFilterChange = (filter: MainFilterOption) => {
    setSelectedMainFilter(filter);
    setSelectedSubFilter("");
    setSelectedChannels([]);
    setAccountSearchTerm("");

    if (filter === "Channel") {
      // Set SMS as default
      setSelectedChannels(["SMS"]);
      setFilterData({ filter, value: "SMS" });
    } else if (filter === "Campaign Status") {
      setFilterData({ filter, value: "active" });
    } else if (filter === "Setup Preview") {
      setFilterData({ filter, value: "pending" });
    } else if (filter === "Accounts") {
      setFilterData({ filter, value: "" });
    } else {
      setFilterData({ filter: "None", value: "" });
    }

    setIsOpen(true);
  };

  // Handle channel selection in dropdown
  const handleChannelSelect = (channel: string) => {
    let newChannels: string[];
    
    if (selectedChannels.includes(channel)) {
      newChannels = selectedChannels.filter((c) => c !== channel);
    } else {
      newChannels = [...selectedChannels, channel];
    }
    
    setSelectedChannels(newChannels);
    
    // Join selected channels with comma
    const channelValue = newChannels.join(",");
    setFilterData({ filter: "Channel", value: channelValue });
    
    // Keep dropdown open for multi-select
    setChannelDropdownOpen(true);
  };

  // Handle subfilter change for non-Channel filters
  const handleSubFilterChange = (value: SubFilterOption) => {
    setSelectedSubFilter(value);
    
    // Convert Campaign Status values to lowercase before sending to API
    let apiValue = value;
    if (selectedMainFilter === "Campaign Status") {
      apiValue = value.toLowerCase();
    }
    
    setFilterData({ filter: selectedMainFilter, value: apiValue });
    setIsOpen(true);
  };

  // Handle account search term change (will be debounced in parent component)
  const handleAccountSearchChange = (value: string) => {
    setAccountSearchTerm(value);
    setFilterData({ filter: "Accounts", value: value });
  };

  // Get display text for filter button
  const getFilterButtonText = () => {
    if (selectedMainFilter === "None" || !filterData.value) {
      return "Filter";
    }
    
    if (selectedMainFilter === "Channel" && selectedChannels.length > 0) {
      return `${selectedMainFilter}: ${selectedChannels.join(", ")}`;
    }
    
    // For Campaign Status, display with proper case
    if (selectedMainFilter === "Campaign Status") {
      const displayValue = filterData.value.charAt(0).toUpperCase() + filterData.value.slice(1);
      return `${selectedMainFilter}: ${displayValue}`;
    }
    
    // For Setup Preview, format display values
    if (selectedMainFilter === "Setup Preview") {
      let displayValue = "";
      switch (filterData.value) {
        case "inreview":
          displayValue = "In Review";
          break;
        case "inreview with warning symbol":
          displayValue = "In Review With Warning";
          break;
        case "partially live":
          displayValue = "Partially Live";
          break;
        case "live with warning symbol":
          displayValue = "Live With Warning";
          break;
        case "rejected with warning symbol":
          displayValue = "Rejected With Warning";
          break;
        default:
          displayValue = filterData.value.charAt(0).toUpperCase() + filterData.value.slice(1);
      }
      return `${selectedMainFilter}: ${displayValue}`;
    }
    
    if (selectedMainFilter === "Accounts" && accountSearchTerm) {
      const maxLength = 20;
      const displayValue = accountSearchTerm.length > maxLength 
        ? `${accountSearchTerm.substring(0, maxLength)}...` 
        : accountSearchTerm;
      return `${selectedMainFilter}: ${displayValue}`;
    }
    
    return `${selectedMainFilter}: ${filterData.value}`;
  };

  // Get display text for channel dropdown
  const getChannelDisplayText = () => {
    if (selectedChannels.length === 0) {
      return "Select channels";
    }
    return selectedChannels.join(", ");
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full font-medium text-[14px] py-2 px-3 !mt-0"
        >
          <FiFilter
            style={{ width: "14px", height: "14px" }}
            className="mr-1"
          />
          {getFilterButtonText()}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent 
        className="w-64 p-3 rounded-md shadow-md space-y-4"
        side="left"
        align="start"
        sideOffset={8}
      >
        {/* Main Filter */}
        <div className="flex w-full">
          <div className="basis-1/3 flex justify-center items-center">
            <Label className="text-sm text-[#020617]">Criteria</Label>
          </div>
          <div className="basis-2/3 flex justify-center items-center">
            <Select
              value={selectedMainFilter}
              onValueChange={(value) =>
                handleMainFilterChange(value as MainFilterOption)
              }
            >
              <SelectTrigger className="text-gray-500 w-full h-8">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Channel" className="cursor-pointer">
                  Channel
                </SelectItem>
                <SelectItem value="Campaign Status" className="cursor-pointer">
                  Campaign Status
                </SelectItem>
                <SelectItem value="Setup Preview" className="cursor-pointer">
                  Setup Preview
                </SelectItem>
                <SelectItem value="Accounts" className="cursor-pointer">
                  Accounts
                </SelectItem>
                <SelectItem value="None" className="cursor-pointer">
                  None
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Subfilter */}
        {selectedMainFilter !== "None" && (
          <div className="flex w-full">
            <div className="basis-1/3 flex justify-center items-center">
              <Label className="text-sm text-[#020617]">Subfilter</Label>
            </div>
            <div className="basis-2/3 flex justify-center items-center">
              {selectedMainFilter === "Channel" && (
                <Select
                  open={channelDropdownOpen}
                  onOpenChange={setChannelDropdownOpen}
                  value={selectedChannels.length > 0 ? "selected" : ""}
                >
                  <SelectTrigger className="text-gray-500 w-[150px] h-8">
                    <SelectValue>
                      <span className={selectedChannels.length === 0 ? "text-gray-500" : ""}>
                        {getChannelDisplayText()}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <div 
                      className="flex items-center space-x-2 px-2 py-1.5 cursor-pointer hover:bg-gray-100"
                      onClick={(e) => {
                        e.preventDefault();
                        handleChannelSelect("SMS");
                      }}
                    >
                      <Checkbox
                        id="sms-dropdown-admin"
                        checked={selectedChannels.includes("SMS")}
                        onCheckedChange={() => handleChannelSelect("SMS")}
                      />
                      <label
                        htmlFor="sms-dropdown-admin"
                        className="text-sm font-medium leading-none cursor-pointer flex-1"
                      >
                        SMS
                      </label>
                    </div>
                    <div 
                      className="flex items-center space-x-2 px-2 py-1.5 cursor-pointer hover:bg-gray-100"
                      onClick={(e) => {
                        e.preventDefault();
                        handleChannelSelect("WhatsApp");
                      }}
                    >
                      <Checkbox
                        id="whatsapp-dropdown-admin"
                        checked={selectedChannels.includes("WhatsApp")}
                        onCheckedChange={() => handleChannelSelect("WhatsApp")}
                      />
                      <label
                        htmlFor="whatsapp-dropdown-admin"
                        className="text-sm font-medium leading-none cursor-pointer flex-1"
                      >
                        WhatsApp
                      </label>
                    </div>
                  </SelectContent>
                </Select>
              )}

              {selectedMainFilter === "Campaign Status" && (
                <Select
                  value={selectedSubFilter}
                  onValueChange={(value) =>
                    handleSubFilterChange(value as SubFilterOption)
                  }
                >
                  <SelectTrigger className="text-gray-500 w-[150px] h-8">
                    <SelectValue placeholder="Select status">
                      <span className="truncate block">
                        {selectedSubFilter || "Select status"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active" className="cursor-pointer">
                      Active
                    </SelectItem>
                    <SelectItem value="Paused" className="cursor-pointer">
                      Paused
                    </SelectItem>
                    <SelectItem value="Completed" className="cursor-pointer">
                      Completed
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {selectedMainFilter === "Setup Preview" && (
                <Select
                  value={selectedSubFilter}
                  onValueChange={(value) =>
                    handleSubFilterChange(value as SubFilterOption)
                  }
                >
                  <SelectTrigger className="text-gray-500 w-[150px] h-8">
                    <SelectValue placeholder="Select preview">
                      <span className="truncate block">
                        {selectedSubFilter === "inreview"
                          ? "In Review"
                          : selectedSubFilter === "inreview with warning symbol"
                          ? "In Review With Warning"
                          : selectedSubFilter === "partially live"
                          ? "Partially Live"
                          : selectedSubFilter === "live with warning symbol"
                          ? "Live With Warning"
                          : selectedSubFilter === "rejected with warning symbol"
                          ? "Rejected With Warning"
                          : selectedSubFilter
                              ? selectedSubFilter.charAt(0).toUpperCase() + selectedSubFilter.slice(1)
                              : "Select preview"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending" className="cursor-pointer">
                      Pending
                    </SelectItem>
                    <SelectItem value="rejected" className="cursor-pointer">
                      Rejected
                    </SelectItem>
                    <SelectItem value="inreview" className="cursor-pointer">
                      In Review
                    </SelectItem>
                    <SelectItem value="live" className="cursor-pointer">
                      Live
                    </SelectItem>
                    <SelectItem value="partially live" className="cursor-pointer">
                      Partially Live
                    </SelectItem>
                    <SelectItem value="live with warning symbol" className="cursor-pointer">
                      Live with Warning
                    </SelectItem>
                    <SelectItem value="inreview with warning symbol" className="cursor-pointer">
                      In Review with Warning
                    </SelectItem>
                    <SelectItem value="rejected with warning symbol" className="cursor-pointer">
                      Rejected with Warning
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {selectedMainFilter === "Accounts" && (
                <Input
                  type="text"
                  placeholder="Search by accounts"
                  value={accountSearchTerm}
                  onChange={(e) => handleAccountSearchChange(e.target.value)}
                  className="w-[150px] h-8 text-sm"
                />
              )}
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AdminCampaignListDropdown;